/**
 * **Ein einziger AudioContext für Aufnahme und Wiedergabe.**
 *
 * Vorher hatten Mikrofon und Wiedergabe je einen eigenen. Am Rechner fällt das
 * nicht auf, auf dem Telefon schon: Sobald `getUserMedia` läuft, schaltet iOS
 * die Audioausgabe auf den Sprachpfad um — andere Route, andere Rate. Ein
 * zweiter Context daneben wird dabei mitgezogen, und was aus ihm herauskommt,
 * knackt. Ein gemeinsamer Context macht diesen Wechsel einmal mit, statt
 * zweimal gegeneinander.
 *
 * Der zweite Grund ist die Geste: Ein AudioContext, der nicht während einer
 * Berührung entsteht, startet auf dem Telefon `suspended` und bleibt stumm, bis
 * er ausdrücklich fortgesetzt wird. Deshalb wird er beim Druck auf „Sprechen
 * starten" angelegt und danach nur noch angehalten und fortgesetzt — nie
 * geschlossen und neu gebaut, denn das nächste Mal gäbe es keine Geste mehr.
 */
let geteilt: AudioContext | null = null

/** Legt den gemeinsamen Context an oder gibt den bestehenden zurück. */
export function getAudioContext(): AudioContext {
  if (!geteilt || geteilt.state === 'closed') geteilt = new AudioContext()
  return geteilt
}

/**
 * Fortsetzen, falls angehalten. Muss aus einer Berührung heraus aufgerufen
 * werden — sonst lehnt das Telefon ab, ohne dass man es merkt.
 */
export async function resumeAudioContext(): Promise<AudioContext> {
  const ctx = getAudioContext()
  if (ctx.state === 'suspended') await ctx.resume()
  return ctx
}

/**
 * Am Ende eines Gesprächs nur anhalten, nicht schließen: Ein angehaltener
 * Context lässt sich beim nächsten Tastendruck fortsetzen, ein geschlossener
 * ist weg und der neue braucht wieder eine Geste.
 */
export function suspendAudioContext(): void {
  if (geteilt && geteilt.state === 'running') void geteilt.suspend()
}

/** Nur für Tests: den gemerkten Context vergessen. */
export function resetAudioContextForTests(): void {
  geteilt = null
}
